// Les accords des parents (photos, transport) et les documents signés en ligne (D-168, D-169) : les mots et les règles
// côté app. La base décide (migration 20261107200000_accords.sql : le plus protecteur, les défauts, qui peut emmener qui) ;
// ici, ce que les écrans disent, accordé au prénom de l'enfant (agree), sans écriture inclusive (D-008).
import { agree, type TeamGender } from './labels';

export type Photo = 'oui' | 'app' | 'non';
export type Ride = 'moi' | 'equipe' | 'tous';
/** Sur la photo d'un match : son choix, ou « pas encore de réponse » (compté comme « non »). */
export type PhotoState = Photo | 'none';
export type RideState = Ride | 'none';

const PHOTO_RANK: Record<Photo, number> = { non: 0, app: 1, oui: 2 };
const RIDE_RANK: Record<Ride, number> = { moi: 0, equipe: 1, tous: 2 };

/** Deux parents répondent autrement : le plus protecteur s'applique (pour assouplir, il faut les deux). */
export const strictestPhoto = (...v: (Photo | null | undefined)[]): Photo | null =>
  v.filter((x): x is Photo => !!x).sort((a, b) => PHOTO_RANK[a] - PHOTO_RANK[b])[0] ?? null;
export const strictestRide = (...v: (Ride | null | undefined)[]): Ride | null =>
  v.filter((x): x is Ride => !!x).sort((a, b) => RIDE_RANK[a] - RIDE_RANK[b])[0] ?? null;

/** Sans réponse : photos « non », transport « les parents de l'équipe » (D-169). */
export const DEFAULT_PHOTO: Photo = 'non';
export const DEFAULT_RIDE: Ride = 'equipe';

/** « Lou », « Lou et Sofia », « Lou, Sofia et Mila ». */
export function listFr(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} et ${names[names.length - 1]}`;
}

/** Les libellés des trois choix, tels que les écrans les montrent. */
export const PHOTO_LABEL: Record<Photo, string> = { oui: 'Oui', app: 'Seulement dans l’app', non: 'Non' };
export const RIDE_LABEL: Record<Ride, string> = { moi: 'Moi seulement', equipe: 'Les parents de l’équipe', tous: 'Tout adulte du covoiturage' };

export interface Who {
  /** Le prénom de l'enfant (ou du licencié). */
  name: string;
  g: TeamGender;
  /** Le licencié majeur répond pour lui-même (tutoiement : « toi »). */
  self?: boolean;
}

/** La question « Les photos » et ses trois choix (s1). */
export function photoQuestion(w: Who): { q: string; opts: { v: Photo; t: string; s: string }[]; note: string } {
  if (w.self) return {
    q: 'Peux-tu être sur les photos du club ?',
    opts: [
      { v: 'oui', t: 'Oui', s: 'Dans l’app, avec l’équipe, et sur la page publique du club (« Victoires en images »).' },
      { v: 'app', t: PHOTO_LABEL.app, s: 'Les photos restent dans l’équipe. Jamais sur la page publique.' },
      { v: 'non', t: 'Non', s: 'Pas de photo de toi, ni dans l’app ni ailleurs.' },
    ],
    note: 'Arceau ne reconnaît personne sur les photos. Avant de publier, celui qui publie voit ton choix et certifie que tu n’es pas sur l’image quand il le faut.',
  };
  return {
    q: `${w.name} peut-${agree(w.g, 'il', 'elle')} être sur les photos du club ?`,
    opts: [
      { v: 'oui', t: 'Oui', s: 'Dans l’app, avec l’équipe et ses parents, et sur la page publique du club (« Victoires en images »).' },
      { v: 'app', t: PHOTO_LABEL.app, s: 'Les photos restent entre l’équipe et ses parents. Jamais sur la page publique.' },
      { v: 'non', t: 'Non', s: `Pas de photo ${agree(w.g, 'de lui', 'd’elle')}, ni dans l’app ni ailleurs.` },
    ],
    note: `Arceau ne reconnaît personne sur les photos. Avant de publier, le coach voit ton choix et certifie que ${w.name} n’est pas sur l’image quand il le faut.`,
  };
}

/** La question « Le transport » et ses trois choix (s1). `other` : l'autre parent (« son père ») ; `team` : « U13 ». */
export function rideQuestion(w: Who, o: { other?: string | null; team?: string | null; players: string }): { q: string; opts: { v: Ride; t: string; s: string }[]; note: string } {
  return {
    q: `Qui peut emmener ${w.name} aux matchs ?`,
    opts: [
      { v: 'moi', t: RIDE_LABEL.moi, s: `Toi ou ${o.other ?? 'l’autre parent'}. Personne d’autre ne peut lui proposer une place.` },
      { v: 'equipe', t: RIDE_LABEL.equipe, s: `Les parents des autres ${o.players}${o.team ? ` des ${o.team}` : ' de l’équipe'} peuvent lui proposer une place.` },
      { v: 'tous', t: RIDE_LABEL.tous, s: 'Aussi un coach, un bénévole ou un autre adulte qui conduit pour le match.' },
    ],
    note: `Chaque place proposée pour ${w.name} te sera demandée : tu réponds « D’accord » ou « Non merci ».`,
  };
}

/** L'autre parent, nommé par son lien : « son père », « sa mère », « l’autre parent ». */
export const otherParentWord = (relation: string | null | undefined): string =>
  /^p[eè]re$/i.test(relation ?? '') ? 'son père' : /^m[eè]re$/i.test(relation ?? '') ? 'sa mère' : 'l’autre parent';

/** La phrase sous un choix dans la fiche de l'enfant (s3). */
export const PHOTO_SUB: Record<Photo, string> = {
  oui: 'Aussi sur la page publique du club', app: 'Jamais sur la page publique du club', non: 'Ni dans l’app, ni ailleurs',
};
export const RIDE_SUB: Record<Ride, string> = {
  moi: 'Personne d’autre ne peut lui proposer une place', equipe: 'Chaque place proposée t’est demandée',
  tous: 'Parents, coachs, bénévoles ; chaque place t’est demandée',
};

/** Ce qui vaut tant que les parents n'ont pas répondu (s3, carte de Lucas). */
export const untilAnswered = (w: Who): string =>
  `D’ici là : pas de photo de ${w.name}, et seuls les parents de l’équipe peuvent lui proposer une place.`;

/** Le message avant de publier une photo (s4) : la raison de chaque prénom cité. */
export function citedReason(state: PhotoState, minor: boolean): string {
  if (state === 'none') return 'Pas encore de réponse de ses parents : c’est non.';
  if (state === 'non') return minor ? 'Ses parents ont dit non aux photos.' : 'Pas de photo, à sa demande.';
  if (state === 'app') return 'Dans l’app seulement.';
  return '';
}

/** La case obligatoire : « Je certifie que Lou et Sofia ne sont pas sur la photo. » */
export function certifyLine(names: string[]): string {
  if (!names.length) return '';
  return `Je certifie que ${listFr(names)} ${names.length > 1 ? 'ne sont pas' : 'n’est pas'} sur la photo.`;
}

/** Pourquoi la page publique n'est pas proposée (s4). */
export function whyNotPublic(appNames: string[]): string {
  if (!appNames.length) return '';
  const who = appNames.length > 1 ? `les parents de ${listFr(appNames)} gardent leurs photos` : `les parents de ${appNames[0]} gardent ses photos`;
  return `Pas proposée pour ce match : ${who} dans l’app, et Arceau ne peut pas savoir qui est sur l’image.`;
}

/** « Pas dans ta liste », sans juger (s5) : la raison neutre d'après l'accord du parent. */
export function notInListWhy(why: Ride | null | undefined, g: TeamGender): string {
  if (why === 'moi') return 'Ses parents l’accompagnent eux-mêmes.';
  return `${agree(g, 'Il', 'Elle')} voyage avec un parent de l’équipe.`;
}

/** La légende de l'effectif (s7). */
export const legendLines = (players: string): { k: string; t: string; s?: string }[] => [
  { k: 'ph:app', t: 'Photos dans l’app seulement', s: 'jamais sur la page publique' },
  { k: 'ph:non', t: 'Pas de photo' },
  { k: 'ride:equipe', t: 'En voiture avec un parent de l’équipe' },
  { k: 'ride:moi', t: 'En voiture avec ses parents seulement' },
  { k: 'none', t: 'Pas encore de réponse', s: 'photo : comme « non » ; voiture : un parent de l’équipe' },
  { k: 'hint', t: `Sans icône : pas de condition. Rien à retenir : la photo et le covoiturage te le rappellent au bon moment. Touche ${players} pour le détail.` },
];

/** Le texte de la case signée : « J’ai lu et j’accepte le règlement intérieur du Basket Club de Rivebelle. » */
export function acceptText(title: string, club: string): string {
  const t = title.trim();
  const low = t.charAt(0).toLowerCase() + t.slice(1);
  const first = low.split(/[\s’']/)[0] ?? '';
  const FEM = ['autorisation', 'charte', 'fiche', 'attestation', 'convention', 'décharge', 'note', 'politique', 'règle', 'demande', 'déclaration', 'feuille', 'procédure'];
  const art = /^[aeiouyhéèêàâîôû]/i.test(low) ? 'l’' : /s$/.test(first) && first.length > 3 ? 'les ' : FEM.includes(first) ? 'la ' : 'le ';
  const c = club.trim();
  const of = /^les\s/i.test(c) ? `des ${c.slice(4)}` : /^[aeiouyhéèêàâîôû]/i.test(c) ? `de l’${c}` : /^(la|le)\s/i.test(c) ? `de ${c}` : `du ${c}`;
  return `J’ai lu et j’accepte ${art}${low} ${of}.`;
}
